// Lean i18n system for Milo. No external deps.
// Usage:
//   import { useT } from "@/lib/i18n";
//   const { t, lang, setLang } = useT();
//
// Language is persisted in localStorage AND on the user record (PATCH /api/me/language).
// Falls back to English when a key is missing from the active dictionary.
//
// IMPORTANT: I18nProvider must be nested INSIDE AuthProvider so it can sync
// language from the authenticated user without making its own /auth/me call
// (which would race with AuthProvider during magic-link verification).

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { en } from "./i18n.en";
import { fr } from "./i18n.fr";
import { api } from "./api";
import { useAuth } from "./auth";

const DICTS = { en, fr };
const SUPPORTED = ["en", "fr"];

const I18nContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

function detectInitialLang() {
    try {
        const saved = localStorage.getItem("milo_lang");
        if (saved && SUPPORTED.includes(saved)) return saved;
        const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
        return SUPPORTED.includes(nav) ? nav : "en";
    } catch {
        return "en";
    }
}

export function I18nProvider({ children }) {
    const [lang, setLangState] = useState(detectInitialLang);
    const auth = useAuth();
    const userLang = auth?.user?.language;

    const setLang = useCallback(async (next, { sync = true } = {}) => {
        if (!SUPPORTED.includes(next)) return;
        setLangState(next);
        try { localStorage.setItem("milo_lang", next); } catch (e) { console.warn("milo_lang persist failed:", e); }
        if (sync) {
            try { await api.patch("/me/language", { language: next }); } catch (e) { console.warn("language sync failed:", e); }
        }
    }, []);

    // When auth resolves, prefer the user's stored language (no separate API call).
    useEffect(() => {
        if (userLang && SUPPORTED.includes(userLang) && userLang !== lang) {
            setLangState(userLang);
            try { localStorage.setItem("milo_lang", userLang); } catch (e) { console.warn("milo_lang persist failed:", e); }
        }
    }, [userLang]);  // eslint-disable-line react-hooks/exhaustive-deps

    const t = useCallback(
        (key, vars) => {
            const dict = DICTS[lang] || DICTS.en;
            let str = dict[key];
            if (str === undefined) str = DICTS.en[key];
            if (str === undefined) return key;
            if (vars) {
                for (const k of Object.keys(vars)) {
                    str = str.replaceAll(`{${k}}`, String(vars[k] ?? ""));
                }
            }
            return str;
        },
        [lang]
    );

    return (
        <I18nContext.Provider value={{ lang, setLang, t, supported: SUPPORTED }}>
            {children}
        </I18nContext.Provider>
    );
}

export function useT() {
    return useContext(I18nContext);
}
